import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import supabase from '../../supabase/supabase';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Input } from '../../components/ui/Input';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';

const { FiPlus, FiEdit2, FiTrash2, FiBox, FiCheck, FiX, FiRefreshCw } = FiIcons;

export const DashboardProducts = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [supplierId, setSupplierId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  useEffect(() => {
    fetchSupplierAndProducts();
  }, []);

  const fetchSupplierAndProducts = async () => {
    setLoading(true);
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return;

    // Get or create supplier profile for this user
    let { data: supplier } = await supabase
      .from('suppliers_1739200000000')
      .select('id')
      .eq('owner_id', user.id)
      .single();

    if (!supplier) {
      const { data: newSupplier, error } = await supabase
        .from('suppliers_1739200000000')
        .insert([{ name: 'My Farm', owner_id: user.id }])
        .select()
        .single();
      supplier = newSupplier;
    }

    setSupplierId(supplier.id);

    const { data: productsData } = await supabase
      .from('products_1739200000000')
      .select('*')
      .eq('supplier_id', supplier.id)
      .order('created_at', { ascending: false });

    setProducts(productsData || []);
    setLoading(false);
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this product?')) return;
    const { error } = await supabase.from('products_1739200000000').delete().eq('id', id);
    if (!error) setProducts(products.filter(p => p.id !== id));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formData = new FormData(e.target);
    const productData = {
      name: formData.get('name'),
      category: formData.get('category'),
      origin: formData.get('origin'),
      price_amount: parseFloat(formData.get('price')),
      price_unit: formData.get('unit'),
      stock_quantity: parseInt(formData.get('stock')),
      is_available: formData.get('available') === 'on',
      image_url: formData.get('image_url') || 'https://images.unsplash.com/photo-1550989460-0adf9ea622e2?auto=format&fit=crop&q=80&w=800',
      supplier_id: supplierId
    };

    if (editingProduct) {
      const { error } = await supabase
        .from('products_1739200000000')
        .update(productData)
        .eq('id', editingProduct.id);
      if (!error) fetchSupplierAndProducts();
    } else {
      const { error } = await supabase.from('products_1739200000000').insert([productData]);
      if (!error) fetchSupplierAndProducts();
    }
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-zinc-900">Product Management</h1>
          <p className="text-zinc-500">Manage your catalog, stock and pricing</p>
        </div>
        <Button onClick={() => { setEditingProduct(null); setIsModalOpen(true); }} className="rounded-full gap-2">
          <SafeIcon icon={FiPlus} /> Add Product
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <SafeIcon icon={FiRefreshCw} className="w-8 h-8 animate-spin text-zinc-300" />
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {products.map((product) => (
            <Card key={product.id} className="group overflow-hidden">
              <div className="aspect-video relative overflow-hidden bg-zinc-100">
                <img src={product.image_url} alt={product.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                <div className="absolute top-4 right-4 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button onClick={() => { setEditingProduct(product); setIsModalOpen(true); }} className="p-2 bg-white rounded-lg shadow-lg hover:text-blue-600 transition-colors">
                    <SafeIcon icon={FiEdit2} className="w-4 h-4" />
                  </button>
                  <button onClick={() => handleDelete(product.id)} className="p-2 bg-white rounded-lg shadow-lg hover:text-red-600 transition-colors">
                    <SafeIcon icon={FiTrash2} className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="p-5">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-widest text-[#D4B483]">{product.category}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${product.is_available ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-600'}`}>
                    {product.is_available ? 'Available' : 'Out of Stock'}
                  </span>
                </div>
                <h3 className="font-bold text-zinc-900 mb-1">{product.name}</h3>
                <div className="flex items-center justify-between mt-4">
                  <div>
                    <p className="text-xs text-zinc-400">Price</p>
                    <p className="font-bold text-zinc-900">€{product.price_amount} <span className="text-xs font-normal text-zinc-500">/ {product.price_unit}</span></p>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-zinc-400">Stock</p>
                    <p className="font-bold text-zinc-900">{product.stock_quantity}</p>
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={() => setIsModalOpen(false)} className="absolute inset-0 bg-zinc-900/60 backdrop-blur-sm" />
            <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} exit={{ opacity: 0, scale: 0.95, y: 20 }} className="relative w-full max-w-xl bg-white rounded-3xl shadow-2xl overflow-hidden" >
              <div className="p-8 border-b border-zinc-100 flex items-center justify-between">
                <h2 className="text-xl font-bold">{editingProduct ? 'Edit Product' : 'Add New Product'}</h2>
                <button onClick={() => setIsModalOpen(false)} className="p-2 hover:bg-zinc-100 rounded-full transition-colors">
                  <SafeIcon icon={FiX} />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-8 space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <Input name="name" label="Product Name" defaultValue={editingProduct?.name} required />
                  <Input name="category" label="Category" defaultValue={editingProduct?.category} required />
                </div>
                <Input name="image_url" label="Image URL" placeholder="https://..." defaultValue={editingProduct?.image_url} />
                <div className="grid grid-cols-3 gap-4">
                  <Input name="price" label="Price (€)" type="number" step="0.01" defaultValue={editingProduct?.price_amount} required />
                  <Input name="unit" label="Unit (e.g. kg)" defaultValue={editingProduct?.price_unit} required />
                  <Input name="stock" label="Stock Qty" type="number" defaultValue={editingProduct?.stock_quantity} required />
                </div>
                <Input name="origin" label="Origin" placeholder="e.g. Utrecht, NL" defaultValue={editingProduct?.origin} />
                <div className="flex items-center gap-2">
                  <input type="checkbox" name="available" id="available" defaultChecked={editingProduct ? editingProduct.is_available : true} className="w-4 h-4 rounded border-zinc-300 text-zinc-900 focus:ring-zinc-900" />
                  <label htmlFor="available" className="text-sm font-medium text-zinc-700">Currently available for order</label>
                </div>
                <div className="pt-4 flex gap-3">
                  <Button type="button" variant="ghost" className="flex-1" onClick={() => setIsModalOpen(false)}>Cancel</Button>
                  <Button type="submit" className="flex-[2]"> {editingProduct ? 'Save Changes' : 'Create Product'} </Button>
                </div>
              </form>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};